import React, { useMemo } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Network, AlertTriangle, ArrowLeft } from 'lucide-react';
import { NextStepButton, StepProgressIndicator } from '../components/WorkflowNavigation';

export default function FailureNetPage() {
    const { allData } = useAppContext();

    const failureChains = useMemo(() => {
        // AIAG-VDA Failure Chain: Effects <- Failure Mode <- Causes
        // For DFMEA, we often just have Effects and Mode in the base FMEA Item.
        // Causes might be nested or inferred. We map standard FMEA structure visually.

        return allData.fmeaItems.map(item => ({
            id: item.id,
            mode: item.failureMode,
            effect: item.effects,
            cause: item.notes || 'Cause not documented'
        }));
    }, [allData.fmeaItems]);

    return (
        <div className="p-6 h-full flex flex-col bg-background/50">
            <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-3">
                    <div className="bg-red-500/10 p-2 rounded-lg text-red-500">
                        <Network size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold font-heading text-text-primary">Failure Network</h1>
                        <p className="text-text-secondary mt-1">AIAG-VDA Step 4: Failure Chains & Analysis</p>
                    </div>
                </div>
                <div className="hidden lg:block">
                    <StepProgressIndicator currentStep={4} completedSteps={[1, 2, 3]} />
                </div>
            </div>

            <div className="flex-1 bg-card rounded-xl border border-border shadow-sm overflow-hidden flex flex-col">
                <div className="overflow-x-auto p-4 flex-1">
                    {failureChains.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary p-8 bg-background/50 rounded-lg border border-dashed border-border/50">
                            <AlertTriangle size={40} className="mb-4 opacity-30" />
                            <p className="font-medium text-lg">No Failures Mapped</p>
                        </div>
                    ) : (
                        <div className="space-y-4 min-w-[700px]">
                            <div className="flex text-xs font-bold text-text-muted uppercase mb-2 px-2">
                                <span className="w-1/3 text-center">Failure Effect (FE)</span>
                                <span className="w-6 flex-shrink-0"></span>
                                <span className="w-1/3 text-center">Failure Mode (FM)</span>
                                <span className="w-6 flex-shrink-0"></span>
                                <span className="w-1/3 text-center">Failure Cause (FC)</span>
                            </div>

                            {failureChains.map(chain => (
                                <div key={chain.id} className="flex items-center gap-2 p-3 bg-background border border-border rounded-lg shadow-sm hover:border-red-500/50 transition-colors">
                                    <div className="w-1/3 p-3 bg-rose-50 dark:bg-rose-900/20 text-rose-900 dark:text-rose-100 rounded-lg border border-rose-200 dark:border-rose-800 break-words font-medium shadow-inner">
                                        {chain.effect || <span className="text-rose-900/40 italic">Not defined</span>}
                                    </div>
                                    <ArrowLeft size={20} className="text-rose-400 p-0.5" strokeWidth={3} />
                                    <div className="w-1/3 p-4 bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100 rounded-lg border-2 border-red-300 dark:border-red-700 break-words font-bold shadow-md text-center transform scale-105 z-10 relative">
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[10px] uppercase px-2 py-0.5 rounded-full z-20 shadow-sm leading-tight border border-red-600 whitespace-nowrap">Core Default</div>
                                        {chain.mode || <span className="text-red-900/40 italic font-normal">Not defined</span>}
                                    </div>
                                    <ArrowLeft size={20} className="text-rose-400 p-0.5" strokeWidth={3} />
                                    <div className="w-1/3 p-3 bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-100 rounded-lg border border-orange-200 dark:border-orange-800 break-words font-medium shadow-inner">
                                        {chain.cause}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Continue to Next Step */}
            <div className={`p-4 mt-6 rounded-lg border flex items-center justify-between bg-card border-border`}>
                <div>
                    <h3 className="font-semibold text-text-primary">Ready to continue?</h3>
                    <p className="text-sm text-text-secondary">Proceed to conduct risk analysis</p>
                </div>
                <NextStepButton currentStep={4} isComplete={failureChains.length > 0} />
            </div>
        </div>
    );
}
